import { useEffect, useState } from 'react'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/controls'
import { Modal } from '../ui/modal'
import { Notice, Spinner } from './LegalPresentation'
import dayjs from 'dayjs'
import {
  getCurrentMerchantAgreement,
  getMerchantAgreementSubmissionParty,
  type AgreementSelection,
  type AgreementSubmissionType,
  type MerchantAgreementDocument,
  type MerchantAgreementSubmissionParty,
} from '../../services/merchantAgreement'
import MerchantAgreementDocumentView from './MerchantAgreementDocumentView'
import { buildMerchantAgreementScheduleA } from './merchantAgreementScheduleA'

interface MerchantAgreementConsentProps {
  open: boolean
  candidateLegalName: string
  authorizedUserName: string
  submissionType: AgreementSubmissionType
  merchantAccountId: string
  registeredEmail: string
  refreshKey: number
  confirming: boolean
  onCancel: () => void
  onConfirm: (selection: AgreementSelection) => void
}

export default function MerchantAgreementConsent({
  open,
  candidateLegalName,
  authorizedUserName,
  submissionType,
  merchantAccountId,
  registeredEmail,
  refreshKey,
  confirming,
  onCancel,
  onConfirm,
}: MerchantAgreementConsentProps) {
  const [document, setDocument] = useState<MerchantAgreementDocument | null>(
    null,
  )
  const [submissionParty, setSubmissionParty] =
    useState<MerchantAgreementSubmissionParty | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [consented, setConsented] = useState(false)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setLoadFailed(false)
    setDocument(null)
    setSubmissionParty(null)
    setConsented(false)

    Promise.all([
      getCurrentMerchantAgreement(),
      getMerchantAgreementSubmissionParty(submissionType, candidateLegalName),
    ])
      .then(([documentResponse, partyResponse]) => {
        if (cancelled) return
        setDocument(documentResponse.data)
        setSubmissionParty(partyResponse.data)
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [candidateLegalName, open, refreshKey, retryKey, submissionType])

  const reusableAcceptanceId = submissionParty?.reusableAcceptanceId
  const displayedAuthorizedUserName =
    (submissionParty?.reusableAcceptanceId
      ? submissionParty.reusableAuthorizedUserName
      : authorizedUserName
    )?.trim() ?? ''
  const canConfirm =
    !!document &&
    !!submissionParty &&
    !loadFailed &&
    !!displayedAuthorizedUserName &&
    (!!reusableAcceptanceId || consented)

  const handleConfirm = () => {
    if (!document || !submissionParty || !canConfirm) return

    if (submissionParty.reusableAcceptanceId) {
      onConfirm({ agreementAcceptanceId: submissionParty.reusableAcceptanceId })
      return
    }

    onConfirm({
      agreementConsent: {
        accepted: true,
        agreementVersion: document.agreementVersion,
        effectiveDate: document.effectiveDate,
        renderedDocumentSha256: document.renderedDocumentSha256,
        partyFingerprint: submissionParty.partyFingerprint,
      },
    })
  }

  return (
    <Modal
      open={open}
      className="merchant-agreement-dialog max-w-5xl"
      title={
        <div className="merchant-agreement-modal-title">
          KyrenPay Merchant Services Agreement
        </div>
      }
      onOpenChange={(value) => {
        if (!value) onCancel()
      }}
      closable={!confirming}
      footer={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onCancel} disabled={confirming}>
            返回修改資料
          </Button>
          <Button
            variant="default"
            disabled={confirming || !canConfirm}
            onClick={handleConfirm}
          >
            同意協議並提交認證
          </Button>
        </div>
      }
    >
      {loading ? (
        <div
          style={{
            minHeight: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner />
        </div>
      ) : loadFailed || !document || !submissionParty ? (
        <Notice
          tone="error"
          title="協議或簽署者資料暫時無法載入"
          body={
            <Button
              variant="link"
              onClick={() => setRetryKey((key) => key + 1)}
            >
              重新載入
            </Button>
          }
        />
      ) : (
        <div className="agreement-consent-content flex min-h-0 flex-col gap-2">
          <Notice
            tone="info"
            title="本次协议签署者"
            body={
              <div className="flex flex-col gap-1">
                <span>
                  {displayedAuthorizedUserName}（
                  {submissionParty.partyType === 'BUSINESS'
                    ? '公司法人'
                    : '個人'}
                  ）
                </span>
                {submissionParty.partyType === 'BUSINESS' && (
                  <span>代表商戶：{submissionParty.merchantLegalName}</span>
                )}
                <strong>
                  请确认由上述签署者本人操作，并有权代表商户接受本协议
                </strong>
                <strong>请阅读协议，并在协议底部勾选同意</strong>
              </div>
            }
          />
          <span className="text-muted-foreground">
            版本 {document.agreementVersion} · Effective Date：
            {document.effectiveDateDisplay}
          </span>
          <div
            className="merchant-agreement-scroll"
            data-testid="merchant-agreement-modal-body"
          >
            <MerchantAgreementDocumentView
              markdownContent={document.markdownContent}
              scheduleAValues={buildMerchantAgreementScheduleA({
                agreementVersion: document.agreementVersion,
                merchantAccountId,
                merchantLegalName: submissionParty.merchantLegalName,
                authorizedUserName: displayedAuthorizedUserName,
                registeredEmail,
                reusableAcceptanceId: submissionParty.reusableAcceptanceId,
                acceptedAt: submissionParty.acceptedAt,
              })}
            />
            <div className="merchant-agreement-confirmation">
              {submissionParty.reusableAcceptanceId ? (
                <Notice
                  tone="success"
                  title={
                    submissionParty.acceptedAt
                      ? `已於 ${dayjs(submissionParty.acceptedAt).format('YYYY-MM-DD HH:mm')} 接受此版本協議`
                      : '已接受此版本協議'
                  }
                  body={`本次認證將復用協議簽署人「${displayedAuthorizedUserName}」的接受記錄。`}
                />
              ) : (
                <Checkbox checked={consented} onCheckedChange={setConsented}>
                  {document.acceptanceStatement}
                </Checkbox>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
